import { NgClass } from '@angular/common';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  NgxIntlTelInputModule,
  CountryISO,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { PublicService } from '../../../../core/services/public.service';
import { ConfirmFeedbackComponent } from '../../../flights/components/popups/confirm-feedback/confirm-feedback.component';
import { EmojiType } from '../../../../shared/enums/emoji-type.enum';
import { SweetAlertService } from '../../../../core/services/sweet-alert.service';

@Component({
  selector: 'app-feedback',
  imports: [
    TranslateModule,
    NgClass,
    NgxIntlTelInputModule,
    ReactiveFormsModule,
    ConfirmFeedbackComponent,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.css',
})
export class FeedbackComponent {
  private fb = inject(FormBuilder);
  private publicService = inject(PublicService);
  private sweetAlert = inject(SweetAlertService);

  @ViewChild('phoneInput', { read: ElementRef }) phoneInputRef!: ElementRef;

  stats = [
    {
      value: '12K',
      label: 'Feedback.State1',
    },
    {
      value: '4.9',
      label: 'Feedback.State2',
    },
    {
      value: '98%',
      label: 'Feedback.State3',
    },
    {
      value: '+2',
      label: 'Feedback.State4',
    },
  ];

  reviews = [
    {
      name: 'Fatima Al-Hassan',
      location: 'Riyadh, Saudi Arabia',
      review: 'Feedback.Feedback1',
      avatar: 'FA',
    },
    {
      name: 'Mohamed Karim',
      location: 'London, UK',
      review: 'Feedback.Feedback2',
      avatar: 'MK',
    },
    {
      name: 'Soraya Benali',
      location: 'Paris, France',
      review: 'Feedback.Feedback3',
      avatar: 'SB',
    },
  ];

  options = [
    { key: 'Feedback.Option1', value: 'Overall Experience' },
    { key: 'Feedback.Option2', value: 'Booking Process' },
    { key: 'Feedback.Option3', value: 'Customer Support' },
    { key: 'Feedback.Option4', value: 'Pricing' },
  ];

  feedbackForm!: FormGroup;
  isSubmitted = false;
  showConfirmationModal = false;
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  isHappy: boolean = true;
  rating: number = 5;

  constructor() {
    this.initForm();
  }

  ngAfterViewInit() {
    const inputEl = this.phoneInputRef?.nativeElement.querySelector('input');
    if (inputEl) {
      inputEl.setAttribute('autocomplete', 'one-time-code');
    }
  }

  initForm(): FormGroup {
    return (this.feedbackForm = this.fb.group({
      senderFirstName: [
        '',
        [Validators.required, Validators.pattern(/^[A-Za-z]+(?: [A-Za-z]+)*$/)],
      ],
      senderLastName: [
        '',
        [Validators.required, Validators.pattern(/^[A-Za-z]+(?: [A-Za-z]+)*$/)],
      ],
      senderEmail: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(
            '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
          ),
        ],
      ],
      countryCode: ['IT', Validators.required],
      mobilePhone: ['', Validators.required],
      emailBody: ['', [Validators.required, Validators.minLength(10)]],
      emailSubject: ['', Validators.required],
      emojiType: ['', Validators.required],
    }));
  }

  getControl(controlName: string): FormControl {
    return this.feedbackForm.get(controlName) as FormControl;
  }

  toggleSubject(value: string) {
    const control = this.feedbackForm.get('emailSubject')!;
    control.setValue(control.value === value ? '' : value);
  }

  onSubmit() {
    this.isSubmitted = true;
    this.feedbackForm.markAllAsTouched();

    if (this.feedbackForm.invalid) return;

    const emoji = this.feedbackForm.get('emojiType')?.value;
    this.isHappy = ![EmojiType.VERY_BAD, EmojiType.BAD].includes(emoji);

    const ratingMap: Record<string, number> = {
      [EmojiType.VERY_BAD]: 1,
      [EmojiType.BAD]: 2,
      [EmojiType.SATISFIED]: 3,
      [EmojiType.GOOD]: 4,
      [EmojiType.VERY_GOOD]: 5,
    };
    this.rating = ratingMap[emoji] || 5;
    this.showConfirmationModal = true;
  }

  resetForm() {
    this.feedbackForm.reset();
  }

  buildPayload() {
    const formValue = this.feedbackForm.value;

    const payload = {
      ...formValue,
      mobilePhone: formValue.mobilePhone?.nationalNumber?.replace(/\s/g, ''),
      countryCode: formValue.mobilePhone?.dialCode,
    };
    return payload;
  }

  confirmForm() {
    const payload = this.buildPayload();
    this.publicService.feedback(payload).subscribe({
      next: (res) => {
        this.showConfirmationModal = false;
        this.feedbackForm.reset();
      },
      error: (err) => {
        this.showConfirmationModal = false;
        this.sweetAlert.sweetErrors('Error', err);
      },
    });
  }

  closeModal() {
    this.showConfirmationModal = false;
  }
}
